import { ConnectProto, JobType, jobTypeToNumber } from "@racoon/api";
import { getEnumName } from "@racoon/lib";
import { DraftJob, makeDraftJob } from "~/features/assign-route/model/draft-jobs";
import { getJobCategoryForJobType } from "~/pages/dashboard/routes/hooks/msw-route-list/jobCategoryRouteCompatibility";
import { scheduleCoversDate, scheduleDivisionId } from "~/shared/lib/container-group-schedule";

export const containerGroupTotalVolume = (cg: ConnectProto.ContainerGroup): number => {
  const unit = cg.containerType?.volume ?? 0;
  // Round to 2 decimals: binary floats make 1.1 * 3 = 3.3000000000000003.
  return Math.round(unit * (cg.numberOfContainers ?? 0) * 100) / 100;
};

/**
 * Whether a "JOB_TYPE_*" key is one of the ТБО job types («Вывоз ТБО», «Подбор»,
 * «Подбор с объездом на ходу»). They are one vehicle job as far as a truck is
 * concerned: the cleanups pick up piles beside the very containers the truck
 * hauls MSW from, and truck types are configured with JOB_TYPE_MSW alone.
 */
const isMswJobTypeKey = (jobType: string): boolean =>
  getJobCategoryForJobType(jobTypeToNumber(jobType as JobType) as ConnectProto.JobType) ===
  ConnectProto.JobCategory.MSW;

/**
 * Whether a truck whose capability matrix is `truckCapabilities` (a set of
 * "containerTypeId|JOB_TYPE_*" pairs) can service `jobType` on this container
 * type. One ТБО capability stands for all three ТБО job types, so an agreement
 * pricing «Подбор» is offered on the same truck that does «Вывоз ТБО» (truck
 * types rarely list the cleanups explicitly).
 */
export const canTruckServeJobType = (
  truckCapabilities: Set<string>,
  containerTypeId: bigint | undefined,
  jobType: string,
): boolean => {
  if (truckCapabilities.has(`${containerTypeId}|${jobType}`)) return true;
  if (!isMswJobTypeKey(jobType)) return false;
  for (const pair of truckCapabilities) {
    const [capContainerTypeId, capJobType] = pair.split("|");
    if (capContainerTypeId === String(containerTypeId) && isMswJobTypeKey(capJobType)) return true;
  }
  return false;
};

/**
 * The locations' copies of the container groups, indexed by container group
 * id: they carry what the group response omits (owner organization, contract),
 * plus the location each one sits on.
 */
export const indexContainerGroupsByLocation = (locations: ConnectProto.PickupLocation[]) => {
  const locationByCgId = new Map<string, ConnectProto.PickupLocation>();
  const richCgById = new Map<string, ConnectProto.ContainerGroup>();
  locations.forEach((location) => {
    (location.containerGroups ?? []).forEach((cg) => {
      locationByCgId.set(String(cg.id), location);
      richCgById.set(String(cg.id), cg);
    });
  });
  return { locationByCgId, richCgById };
};

/**
 * A group's container groups in display order: the persisted order (the
 * smallest step_idx among a container group's links), overridden by the
 * optimistic order while a drag is being saved. Shared by the table and the
 * map so that a pin's number is the number of its row.
 *
 * With `shownCgIds` (a single-day view), only those container groups, numbered
 * after the cut so that the numbers still agree.
 */
export const orderGroupContainerGroups = (
  group: ConnectProto.PickupLocationGroup,
  localOrder?: string[] | null,
  shownCgIds?: { has(cgId: string): boolean } | null,
): ConnectProto.ContainerGroup[] => {
  const stepIdxByCgId = new Map<string, number>();
  (group.containerGroupLinks ?? []).forEach((link) => {
    const id = String(link.containerGroupId);
    const step = link.stepIdx ?? 0;
    stepIdxByCgId.set(id, Math.min(stepIdxByCgId.get(id) ?? Number.MAX_SAFE_INTEGER, step));
  });

  const orderOf = (cg: ConnectProto.ContainerGroup): number => {
    if (localOrder) {
      const i = localOrder.indexOf(String(cg.id));
      if (i !== -1) return i;
    }
    return stepIdxByCgId.get(String(cg.id)) ?? Number.MAX_SAFE_INTEGER;
  };

  // Bigint-safe id tiebreaker: Number(a.id - b.id) could lose precision.
  return (group.containerGroups ?? [])
    .filter((cg) => !shownCgIds || shownCgIds.has(String(cg.id)))
    .sort((a, b) => orderOf(a) - orderOf(b) || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));
};

type BuildGroupDraftJobsArgs = {
  group: ConnectProto.PickupLocationGroup;
  /** Container group stops of PreviewPickupLocationGroupJobs, in route order. */
  stops: ConnectProto.JobStop[];
  /** Its taken container group stops, drafted after the rest as duplicates. */
  taken?: ConnectProto.JobStop[];
  /** Locations list — carries the pickup location records and the richer
   *  container groups (owner organization, contract) the group response omits. */
  locations: ConnectProto.PickupLocation[];
  serviceProviderId?: bigint;
  authorId?: bigint;
};

/**
 * The jobs not created yet of a route built out of this group: one per
 * container group stop of the day's preview, in its order, as drafts that only
 * exist until the dispatcher submits the route. A taken stop drafts a duplicate
 * of its job (see DraftJob.duplicateOf).
 *
 * Stops whose container group is no longer in the group are skipped — there
 * would be no location to service.
 */
export const buildGroupDraftJobs = ({
  group,
  stops,
  taken = [],
  locations,
  serviceProviderId,
  authorId,
}: BuildGroupDraftJobsArgs): DraftJob[] => {
  const { locationByCgId, richCgById } = indexContainerGroupsByLocation(locations);

  const groupCgById = new Map(
    (group.containerGroups ?? []).map((cg) => [String(cg.id), cg] as const),
  );

  return (
    [
      ...stops.map((stop) => ({ stop, isTaken: false })),
      ...taken.map((stop) => ({ stop, isTaken: true })),
    ]
      .flatMap(({ stop, isTaken }) => {
        const cgId = String(stop.containerGroupId);
        const containerGroup = richCgById.get(cgId) ?? groupCgById.get(cgId);
        if (!containerGroup) return [];

        return [
          {
            containerGroup,
            location: locationByCgId.get(cgId),
            type: stop.jobType,
            twStartAt: stop.twStartAt,
            duplicateOf: isTaken && stop.job ? BigInt(stop.job.id) : undefined,
          },
        ];
      })
      // Indexed after the skips so the synthetic ids stay contiguous.
      .map(({ containerGroup, location, type, twStartAt, duplicateOf }, index) =>
        makeDraftJob({
          index,
          duplicateOf,
          pickupLocation: location,
          containerGroup,
          serviceReceiver: containerGroup.ownerOrganization,
          create: {
            pickupLocationId: containerGroup.pickupLocationId,
            type,
            jobCategory: getJobCategoryForJobType(type),
            containerGroupId: containerGroup.id,
            containerTypeId: containerGroup.containerType?.id,
            pickupLocationGroupId: group.id,
            serviceProviderId,
            // The container group's owner is the customer the job is done for.
            serviceReceiverId: containerGroup.ownerOrganizationId,
            authorId,
            twStartAt,
          },
        }),
      )
  );
};

/** "containerGroupId|JOB_TYPE_*" of a (container group, job type) pair. */
/** A link's job type by its "JOB_TYPE_*" name, the form tariff and schedule maps key by. */
export const linkJobTypeName = (link: ConnectProto.ContainerGroupJobTypeLink): string =>
  getEnumName(ConnectProto.JobTypeSchema, link.jobType) ?? "";

const linkKey = (containerGroupId: bigint, jobType: string) => `${containerGroupId}|${jobType}`;

type JobsOwnedByGroupArgs = {
  group: ConnectProto.PickupLocationGroup;
  /** The company's groups, this one included, with their links. */
  groups: ConnectProto.PickupLocationGroup[];
  jobs: ConnectProto.Job[];
};

/**
 * The jobs among `jobs` that belong to `group`, the way route assembly in the
 * backend reads it (plgOwnedJobsCondition): stamped with the group, or
 * unstamped on a (container group, job type) link that no other group holds.
 * An unstamped job on a shared link belongs to nobody, and a job stamped with
 * another group belongs to that group.
 */
export const jobsOwnedByGroup = ({
  group,
  groups,
  jobs,
}: JobsOwnedByGroupArgs): ConnectProto.Job[] => {
  const heldElsewhere = new Set<string>();
  groups.forEach((other) => {
    if (other.id === group.id) return;
    (other.containerGroupLinks ?? []).forEach((link) =>
      heldElsewhere.add(linkKey(link.containerGroupId, linkJobTypeName(link))),
    );
  });
  const exclusive = new Set(
    (group.containerGroupLinks ?? [])
      .map((link) => linkKey(link.containerGroupId, linkJobTypeName(link)))
      .filter((key) => !heldElsewhere.has(key)),
  );

  return jobs.filter((job) => {
    // An unset stamp reads as 0n.
    if (job.pickupLocationGroupId) return job.pickupLocationGroupId === group.id;
    const jobType = getEnumName(ConnectProto.JobTypeSchema, job.type);
    return (
      job.containerGroup != null &&
      !!jobType &&
      exclusive.has(linkKey(job.containerGroup.id, jobType))
    );
  });
};

type DueJobTypesArgs = {
  group: ConnectProto.PickupLocationGroup;
  /** The group's schedule rows by stringified container group id. */
  schedulesByCgId: Map<string, ConnectProto.ContainerGroupSchedule[]>;
  /** Zones (division ids in order) by stringified container group id: the group
   *  response does not preload them. Missing: the rows are not narrowed by division. */
  zoneByCgId?: Map<string, string[]>;
  /** Live jobs owned by this group (see jobsOwnedByGroup) whose planned time
   *  window starts on `date`. */
  jobs: ConnectProto.Job[];
  date: Date;
};

/**
 * The job types each of the group's container groups is due for on a service
 * day: a link whose schedule row covers the day, or a job of this group
 * planned for it. Container groups due for nothing are absent, so the map's
 * keys are the rows a single-day view shows.
 */
export const dueJobTypesByCgId = ({
  group,
  schedulesByCgId,
  zoneByCgId,
  jobs,
  date,
}: DueJobTypesArgs): Map<string, string[]> => {
  const groupCgById = new Map(
    (group.containerGroups ?? []).map((cg) => [String(cg.id), cg] as const),
  );
  const due = new Map<string, Set<string>>();
  const add = (cgId: string, jobType: string) => {
    if (!groupCgById.has(cgId)) return;
    due.set(cgId, (due.get(cgId) ?? new Set()).add(jobType));
  };

  // A zoned group's row belongs to the route group of the division serving it.
  const servedByGroupOwner = (cgId: string, schedule: ConnectProto.ContainerGroupSchedule) => {
    const division = scheduleDivisionId(schedule, zoneByCgId?.get(cgId) ?? []);
    return division === undefined || division === String(group.ownerOrganizationId);
  };

  (group.containerGroupLinks ?? []).forEach((link) => {
    const cgId = String(link.containerGroupId);
    const jobType = linkJobTypeName(link);
    const covered = (schedulesByCgId.get(cgId) ?? []).some(
      (schedule) =>
        schedule.jobType === link.jobType &&
        servedByGroupOwner(cgId, schedule) &&
        scheduleCoversDate(schedule, date),
    );
    if (covered) add(cgId, jobType);
  });

  jobs.forEach((job) => {
    const jobType = getEnumName(ConnectProto.JobTypeSchema, job.type);
    if (job.containerGroup == null || !jobType) return;
    add(String(job.containerGroup.id), jobType);
  });

  return new Map([...due].map(([cgId, jobTypes]) => [cgId, [...jobTypes]]));
};

export type GroupStats = {
  containerGroupsCount: number;
  locationsCount: number;
  containersCount: number;
  totalVolume: number;
};

export const computeGroupStats = (containerGroups: ConnectProto.ContainerGroup[]): GroupStats => {
  const locationIds = new Set(containerGroups.map((cg) => String(cg.pickupLocationId)));
  const containersCount = containerGroups.reduce(
    (acc, cg) => acc + (cg.numberOfContainers ?? 0),
    0,
  );
  const totalVolume = containerGroups.reduce((acc, cg) => acc + containerGroupTotalVolume(cg), 0);
  return {
    containerGroupsCount: containerGroups.length,
    locationsCount: locationIds.size,
    containersCount,
    totalVolume: Math.round(totalVolume * 100) / 100,
  };
};
